import {
  Box,
  Button,
  Container,
  FormField,
  Input,
  SpaceBetween,
  Spinner,
  Table,
  Link,
  Header,
} from '@amzn/awsui-components-react';
import { get } from 'lodash';
import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useIntegrationQuery } from './hooks/useIntegrationQuery';

const IntegrationPage = () => {
  const history = useHistory();
  const [inputValue, setInputValue] = useState('');
  const [getIntegration, { loading, data: integrationData }] = useIntegrationQuery();
  const [sortingColumn, setSortingColumn] = useState<any>({ sortingField: 'DataSourceName' });
  const [isDescending, setIsDescending] = useState(false);

  const handleSubmit = () => {
    const parts = inputValue.split(':');
    const accountId = parts[0];
    const datasourceName = parts[1] || null;

    getIntegration({
      variables: {
        accountId,
        datasourceName,
      },
    });
  };

  const datasources = get(integrationData, 'integration', []);
  const inputParts = inputValue.split(':');
  const hasSpecificDatasource = inputParts.length > 1 && inputParts[1]?.trim();
  
  const command = hasSpecificDatasource
    ? `aes dqs get-datasource -c ${inputParts[0]} -d ${inputParts[1]}`
    : `aes dqs get-datasource -c ${inputParts[0]}`;

  // Navigate to details page if specific datasource was queried
  useEffect(() => {
    if (datasources && datasources.length === 1 && hasSpecificDatasource) {
      const ds = datasources[0];
      history.push(`/integration/${ds.ClientId}/${ds.DataSourceName}`);
    }
  }, [datasources, hasSpecificDatasource, history]);

  const handleDatasourceClick = (clientId: string, datasourceName: string) => {
    history.push(`/integration/${clientId}/${datasourceName}`);
  };

  // Check if datasources is actually an array
  const datasourceList = Array.isArray(datasources) ? datasources : [];

  // Sort datasources
  const sortedDatasources = [...datasourceList].sort((a, b) => {
    const field = sortingColumn.sortingField;
    const aVal = a[field] || '';
    const bVal = b[field] || '';
    
    if (isDescending) {
      return bVal.localeCompare(aVal);
    }
    return aVal.localeCompare(bVal);
  });

  return (
    <SpaceBetween size="l">
      <Header variant="h1" description="Query datasources by Account ID or Account ID with Datasource Name">
        <span style={{ color: '#0972d3' }}>DataSources</span>
      </Header>

      <Container>
        <SpaceBetween direction="vertical" size="l">
          <FormField
            label={<strong>Search DataSources</strong>}
            description="Enter Account ID (e.g., 131436254860) or Account ID with Datasource Name (e.g., 131436254860:mycwl1)"
            constraintText="Format: accountId or accountId:datasourceName"
          >
            <SpaceBetween direction="horizontal" size="xs">
              <Input
                onChange={({ detail }) => setInputValue(detail.value)}
                value={inputValue}
                inputMode="text"
                placeholder="131436254860 or 131436254860:datasourceName"
                disabled={loading}
              />
              <Button 
                variant="primary" 
                onClick={handleSubmit} 
                disabled={!inputValue.trim() || loading}
                loading={loading}
              >
                Search
              </Button>
            </SpaceBetween>
          </FormField>

          {inputValue && (
            <Box variant="awsui-key-label">
              CLI Command: <Box variant="code">{command}</Box>
            </Box>
          )}
        </SpaceBetween>
      </Container>

      {loading && (
        <Container>
          <Box textAlign="center" padding={{ vertical: 'xxl' }}>
            <Spinner size="large" />
            <Box variant="p" padding={{ top: 's' }}>
              Loading datasources...
            </Box>
          </Box>
        </Container>
      )}

      {!loading && datasourceList && datasourceList.length > 0 && !hasSpecificDatasource && (
        <Table
          columnDefinitions={[
            {
              id: 'datasourceName',
              header: 'Datasource Name',
              cell: (item: any) => (
                <Link onFollow={() => handleDatasourceClick(item.ClientId, item.DataSourceName)}>
                  {item.DataSourceName}
                </Link>
              ),
              sortingField: 'DataSourceName',
            },
            {
              id: 'connector',
              header: 'Connector',
              cell: (item: any) => item.Connector || 'N/A',
              sortingField: 'Connector',
            },
            {
              id: 'description',
              header: 'Description',
              cell: (item: any) => item.Description || 'N/A',
            },
            {
              id: 'createdTime',
              header: 'Created Time',
              cell: (item: any) => item.CreatedTime || 'N/A',
              sortingField: 'CreatedTime',
            },
          ]}
          items={sortedDatasources}
          loadingText="Loading datasources"
          sortingColumn={sortingColumn}
          sortingDescending={isDescending}
          onSortingChange={({ detail }) => {
            setSortingColumn(detail.sortingColumn);
            setIsDescending(detail.isDescending || false);
          }}
          header={
            <Header
              counter={`(${datasourceList.length})`}
              description="Click on a datasource name to view details"
            >
              DataSources
            </Header>
          }
          empty={
            <Box textAlign="center" color="inherit" padding={{ vertical: 'xxl' }}>
              <SpaceBetween size="m">
                <b>No datasources found</b>
                <Box variant="p" color="inherit">
                  Try searching with a different Account ID
                </Box>
              </SpaceBetween>
            </Box>
          }
        />
      )}
    </SpaceBetween>
  );
};

export { IntegrationPage };
